/**
 * useMetronome Hook
 * -----------------
 * Provides:
 *  1. Audible metronome click (accented on beat 1)
 *  2. Visual beat counter (current beat number)
 *  3. Timing evaluation using FIXED MILLISECOND WINDOWS
 *     (not percentage-based — human reaction time doesn't scale with note length)
 *
 * Tolerance presets:
 *  - Relaxed:  generous windows for beginners / sight-reading practice
 *  - Normal:   realistic for trained musicians
 *  - Strict:   tight windows for advanced drilling
 *
 * Based on research: professional studio musicians typically land within
 * 20-50ms of the beat. Skilled amateurs 50-150ms. Sight-reading adds
 * cognitive load, pushing timing wider by ~50-100ms.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { getSharedAudioContext } from "@/lib/audioContext";

export type TolerancePreset = "relaxed" | "normal" | "strict";

export interface TimingWindows {
  perfect: number;    // ms — ±this value = "perfect"
  good: number;       // ms — ±this value = "good"
  acceptable: number; // ms — ±this value = "acceptable" (still counts as on-time)
  late: number;       // ms — beyond acceptable but within this = "early/late"
  // beyond late = "miss"
}

export const TOLERANCE_PRESETS: Record<TolerancePreset, TimingWindows> = {
  relaxed: {
    perfect: 100,
    good: 200,
    acceptable: 350,
    late: 500,
  },
  normal: {
    perfect: 60,
    good: 150,
    acceptable: 250,
    late: 400,
  },
  strict: {
    perfect: 35,
    good: 80,
    acceptable: 150,
    late: 250,
  },
};

export interface TimingResult {
  /** How many ms early (negative) or late (positive) the note was played */
  offsetMs: number;
  /** Absolute deviation in ms */
  absoluteMs: number;
  /** Whether the timing was within the "acceptable" window (counts as on-time) */
  onTime: boolean;
  /** Rating label */
  rating: "perfect" | "good" | "acceptable" | "early" | "late" | "miss";
}

export interface MetronomeState {
  isRunning: boolean;
  currentBeat: number;       // 1-indexed beat within measure (1, 2, 3, 4)
  currentMeasure: number;    // 0-indexed measure number
  globalBeat: number;        // 0-indexed beat from start of exercise
  tempo: number;
  beatsPerMeasure: number;
  elapsedMs: number;         // ms since metronome started
}

interface UseMetronomeReturn {
  state: MetronomeState;
  start: (tempo: number, beatsPerMeasure: number, countIn?: number) => void;
  stop: () => void;
  setMuted: (muted: boolean) => void;
  isMuted: boolean;
  tolerance: TolerancePreset;
  setTolerance: (preset: TolerancePreset) => void;
  /** Evaluate timing: call this when the student plays a note. */
  evaluateTiming: (expectedGlobalBeat: number, expectedDurationBeats: number) => TimingResult;
  /** Get the expected timestamp (ms from start) for a given global beat */
  getBeatTimestamp: (globalBeat: number) => number;
}

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.1;

export function useMetronome(): UseMetronomeReturn {
  const [state, setState] = useState<MetronomeState>({
    isRunning: false,
    currentBeat: 0,
    currentMeasure: 0,
    globalBeat: 0,
    tempo: 72,
    beatsPerMeasure: 4,
    elapsedMs: 0,
  });

  const [isMuted, setIsMuted] = useState(false);
  const [tolerance, setToleranceState] = useState<TolerancePreset>("relaxed");
  const mutedRef = useRef(false);
  const toleranceRef = useRef<TolerancePreset>("relaxed");

  const setTolerance = useCallback((preset: TolerancePreset) => {
    toleranceRef.current = preset;
    setToleranceState(preset);
  }, []);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const beatTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const startTimeRef = useRef(0);
  const wallStartRef = useRef(0);
  const nextBeatTimeRef = useRef(0);
  const globalBeatRef = useRef(0);
  const tempoRef = useRef(72);
  const beatsPerMeasureRef = useRef(4);
  const isRunningRef = useRef(false);
  const countInBeatsRef = useRef(0);

  const getAudioCtx = useCallback(() => getSharedAudioContext(), []);

  const scheduleClick = useCallback((time: number, accent: boolean) => {
    if (mutedRef.current) return;
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = accent ? 1000 : 800;
    gain.gain.setValueAtTime(accent ? 0.15 : 0.08, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + 0.05);
  }, [getAudioCtx]);

  const scheduler = useCallback(() => {
    const ctx = getAudioCtx();
    const beatDuration = 60 / tempoRef.current;

    while (nextBeatTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD_S) {
      const beatInMeasure = globalBeatRef.current % beatsPerMeasureRef.current;
      const isAccent = beatInMeasure === 0;

      scheduleClick(nextBeatTimeRef.current, isAccent);

      const gBeat = globalBeatRef.current;
      const measure = Math.floor(gBeat / beatsPerMeasureRef.current);
      const beat = (gBeat % beatsPerMeasureRef.current) + 1;
      const elapsed = (nextBeatTimeRef.current - startTimeRef.current) * 1000;

      // Defer the visual update until the click actually sounds: beats are
      // scheduled up to SCHEDULE_AHEAD_S early on the audio clock.
      const delayMs = Math.max(0, (nextBeatTimeRef.current - ctx.currentTime) * 1000);
      const timeout = setTimeout(() => {
        beatTimeoutsRef.current.delete(timeout);
        if (!isRunningRef.current) return;
        setState(prev => ({
          ...prev,
          currentBeat: beat,
          currentMeasure: measure,
          globalBeat: gBeat,
          elapsedMs: elapsed,
        }));
      }, delayMs);
      beatTimeoutsRef.current.add(timeout);

      globalBeatRef.current++;
      nextBeatTimeRef.current += beatDuration;
    }
  }, [getAudioCtx, scheduleClick]);

  const start = useCallback((tempo: number, beatsPerMeasure: number, countIn = 1) => {
    const ctx = getAudioCtx();

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    beatTimeoutsRef.current.forEach(clearTimeout);
    beatTimeoutsRef.current.clear();

    tempoRef.current = tempo;
    beatsPerMeasureRef.current = beatsPerMeasure;
    const countInBeats = countIn * beatsPerMeasure;
    countInBeatsRef.current = countInBeats;

    globalBeatRef.current = -countInBeats;
    startTimeRef.current = ctx.currentTime;
    wallStartRef.current = performance.now();
    nextBeatTimeRef.current = ctx.currentTime;
    isRunningRef.current = true;

    setState({
      isRunning: true,
      currentBeat: 1,
      currentMeasure: 0,
      globalBeat: -countInBeats,
      tempo,
      beatsPerMeasure,
      elapsedMs: 0,
    });

    timerRef.current = setInterval(scheduler, LOOKAHEAD_MS);
    scheduler();
  }, [getAudioCtx, scheduler]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    beatTimeoutsRef.current.forEach(clearTimeout);
    beatTimeoutsRef.current.clear();
    isRunningRef.current = false;
    globalBeatRef.current = 0;
    setState(prev => ({ ...prev, isRunning: false, currentBeat: 0, globalBeat: 0 }));
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    mutedRef.current = muted;
    setIsMuted(muted);
  }, []);

  const getBeatTimestamp = useCallback((globalBeat: number): number => {
    const beatDuration = 60000 / tempoRef.current;
    return (globalBeat + countInBeatsRef.current) * beatDuration;
  }, []);

  // Evaluate timing using FIXED MILLISECOND WINDOWS
  const evaluateTiming = useCallback((expectedGlobalBeat: number, _expectedDurationBeats: number): TimingResult => {
    const now = performance.now();
    const elapsedMs = now - wallStartRef.current;
    const expectedMs = getBeatTimestamp(expectedGlobalBeat);
    const offsetMs = elapsedMs - expectedMs;
    const absoluteMs = Math.abs(offsetMs);

    const windows = TOLERANCE_PRESETS[toleranceRef.current];

    let rating: TimingResult["rating"];
    let onTime: boolean;

    if (absoluteMs <= windows.perfect) {
      rating = "perfect";
      onTime = true;
    } else if (absoluteMs <= windows.good) {
      rating = "good";
      onTime = true;
    } else if (absoluteMs <= windows.acceptable) {
      rating = "acceptable";
      onTime = true;
    } else if (absoluteMs <= windows.late) {
      rating = offsetMs < 0 ? "early" : "late";
      onTime = false;
    } else {
      rating = "miss";
      onTime = false;
    }

    return { offsetMs, absoluteMs, onTime, rating };
  }, [getBeatTimestamp]);

  useEffect(() => {
    const beatTimeouts = beatTimeoutsRef.current;
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      beatTimeouts.forEach(clearTimeout);
      beatTimeouts.clear();
    };
  }, []);

  return {
    state,
    start,
    stop,
    setMuted,
    isMuted,
    tolerance,
    setTolerance,
    evaluateTiming,
    getBeatTimestamp,
  };
}
